import React from 'react';
import { Link } from 'react-router-dom';
import { Play, CheckCircle, XCircle, Clock, Plane, Hotel, Car, Bell, BarChart3, Shield, Users, ArrowRight, Briefcase, CreditCard, FileText, Search, LayoutDashboard, Wallet, Settings } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
export function DemoFlowPage() {
  const {
    travelRequests,
    confirmedTrips,
    notifications
  } = useAppContext();
  const pendingCount = travelRequests.filter(r => r.status === 'pending').length;
  const confirmedCount = confirmedTrips.length;
  const unreadCount = notifications.filter(n => !n.read).length;
  const Section = ({
    number,
    title,
    color,
    children
  }: {
    number: string;
    title: string;
    color: string;
    children: React.ReactNode;
  }) => <div className="relative pl-16 pb-12 last:pb-0" data-id="element-1231">
      {/* Vertical Line */}
      <div className="absolute left-6 top-14 bottom-0 w-0.5 bg-slate-200 last:hidden" data-id="element-1232"></div>

      {/* Number Circle */}
      <div className={`absolute left-0 top-0 flex h-12 w-12 items-center justify-center rounded-full border-4 border-white shadow-md text-xl font-bold text-white ${color}`} data-id="element-1233">
        {number}
      </div>

      <div className="mb-6" data-id="element-1234">
        <h2 className="text-2xl font-bold text-slate-800" data-id="element-1235">{title}</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1236">
        {children}
      </div>
    </div>;
  const StepCard = ({
    icon: Icon,
    title,
    desc,
    link,
    linkText = 'Try It',
    badge = null,
    statusColor = 'text-blue-600 bg-blue-50'
  }: {
    icon: any;
    title: string;
    desc: string;
    link?: string;
    linkText?: string;
    badge?: string | number | null;
    statusColor?: string;
  }) => <div className="group relative flex flex-col rounded-xl border border-slate-200 bg-white p-5 shadow-sm transition-all hover:border-blue-300 hover:shadow-md" data-id="element-1237">
      <div className="mb-4 flex items-start justify-between" data-id="element-1238">
        <div className={`rounded-lg p-2.5 ${statusColor}`} data-id="element-1239">
          <Icon className="h-6 w-6" data-id="element-1240" />
        </div>
        {badge && <span className="inline-flex items-center rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-800" data-id="element-1241">
            {badge}
          </span>}
      </div>
      <h3 className="mb-2 font-bold text-slate-900" data-id="element-1242">{title}</h3>
      <p className="mb-4 flex-1 text-sm text-slate-500" data-id="element-1243">{desc}</p>
      {link && <Link to={link} className="inline-flex items-center text-sm font-medium text-blue-600 hover:text-blue-700 group-hover:underline" data-id="element-1244">
          {linkText} <ArrowRight className="ml-1 h-4 w-4" data-id="element-1245" />
        </Link>}
    </div>;
  return <div className="min-h-screen bg-slate-50 pb-20" data-id="element-1246">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 py-12 px-6 sm:px-12 mb-12 rounded-b-3xl shadow-xl" data-id="element-1247">
        <div className="max-w-7xl mx-auto" data-id="element-1248">
          <div className="flex items-center gap-4 mb-4" data-id="element-1249">
            <div className="p-3 bg-white/10 rounded-xl backdrop-blur-sm" data-id="element-1250">
              <Play className="h-8 w-8 text-blue-400" data-id="element-1251" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white" data-id="element-1252">
              Platform Process Flow
            </h1>
          </div>
          <p className="text-lg text-slate-300 max-w-2xl ml-16" data-id="element-1253">
            Interactive walkthrough of the complete travel management lifecycle.
            Follow the steps below to experience the full platform capabilities.
          </p>

          {/* Quick Demo Path */}
          <div className="mt-8 ml-16 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-6" data-id="element-1254">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2" data-id="element-1255">
              <Play className="h-4 w-4 fill-current" data-id="element-1256" /> Suggested Demo Path
            </h3>
            <div className="flex flex-wrap items-center gap-2 text-sm text-slate-200" data-id="element-1257">
              <Link to="/booking" className="hover:text-white hover:underline font-medium" data-id="element-1258">
                1. Book Travel
              </Link>
              <ArrowRight className="h-3 w-3 text-slate-500" data-id="element-1259" />
              <span data-id="element-1260">2. Submit Request</span>
              <ArrowRight className="h-3 w-3 text-slate-500" data-id="element-1261" />
              <Link to="/approvals" className="hover:text-white hover:underline font-medium" data-id="element-1262">
                3. Approve (3 Levels)
              </Link>
              <ArrowRight className="h-3 w-3 text-slate-500" data-id="element-1263" />
              <Link to="/trips" className="hover:text-white hover:underline font-medium" data-id="element-1264">
                4. View Confirmed Trip
              </Link>
              <ArrowRight className="h-3 w-3 text-slate-500" data-id="element-1265" />
              <Link to="/" className="hover:text-white hover:underline font-medium" data-id="element-1266">
                5. Check Dashboard
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6" data-id="element-1267">
        {/* SECTION 1: ONBOARDING */}
        <Section number="1" title="Onboarding & Access" color="bg-slate-600" data-id="element-1268">
          <StepCard icon={Shield} title="Register Organization" desc="Create company account, set up domain, and configure initial settings." link="/register" statusColor="bg-slate-100 text-slate-600" data-id="element-1269" />
          <StepCard icon={Users} title="Role Selection" desc="Login as Super Admin, Company Admin, Manager, or Traveler." link="/login" linkText="Go to Login" statusColor="bg-slate-100 text-slate-600" data-id="element-1270" />
          <StepCard icon={Settings} title="Admin Setup" desc="Configure policies, departments, and invite team members." link="/settings" statusColor="bg-slate-100 text-slate-600" data-id="element-1271" />
        </Section>

        {/* SECTION 2: BOOKING FLOW */}
        <Section number="2" title="Booking Flow" color="bg-blue-600" data-id="element-1272">
          <StepCard icon={Search} title="Search & Compare" desc="Find flights, hotels, and cars with real-time availability and pricing." link="/booking" linkText="Start Booking" statusColor="bg-blue-100 text-blue-600" data-id="element-1273" />
          <StepCard icon={Shield} title="Policy Check" desc="Automated compliance check against company travel policies." link="/booking" linkText="View Policies" statusColor="bg-blue-100 text-blue-600" data-id="element-1274" />
          <StepCard icon={FileText} title="Submit Request" desc="Review details and submit for approval workflow." link="/booking" linkText="Create Request" statusColor="bg-blue-100 text-blue-600" data-id="element-1275" />
        </Section>

        {/* SECTION 3: APPROVAL WORKFLOW */}
        <Section number="3" title="Approval Workflow" color="bg-emerald-600" data-id="element-1276">
          <div className="col-span-full bg-emerald-50/50 border border-emerald-100 rounded-xl p-6 mb-4" data-id="element-1277">
            <div className="flex items-center gap-4 mb-4" data-id="element-1278">
              <div className="p-2 bg-emerald-100 rounded-lg text-emerald-700" data-id="element-1279">
                <CheckCircle className="h-6 w-6" data-id="element-1280" />
              </div>
              <div data-id="element-1281">
                <h3 className="font-bold text-slate-900" data-id="element-1282">
                  Multi-Level Approval Chain
                </h3>
                <p className="text-sm text-slate-500" data-id="element-1283">
                  Requests must pass all 3 levels to be confirmed.
                </p>
              </div>
              <div className="ml-auto" data-id="element-1284">
                <Link to="/approvals" className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700 transition-colors" data-id="element-1285">
                  Manage Approvals{' '}
                  {pendingCount > 0 && <span className="ml-2 bg-white text-emerald-700 px-2 py-0.5 rounded-full text-xs font-bold" data-id="element-1286">
                      {pendingCount}
                    </span>}
                </Link>
              </div>
            </div>

            <div className="relative flex flex-col md:flex-row justify-between items-center gap-4 md:gap-0" data-id="element-1287">
              {/* Connecting Line */}
              <div className="absolute top-1/2 left-0 w-full h-0.5 bg-emerald-200 -z-10 hidden md:block" data-id="element-1288"></div>

              <div className="bg-white p-4 rounded-lg border border-emerald-200 shadow-sm w-full md:w-64 text-center relative" data-id="element-1289">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-100 text-emerald-800 text-xs font-bold px-2 py-0.5 rounded-full" data-id="element-1290">
                  Level 1
                </div>
                <Users className="h-8 w-8 text-emerald-500 mx-auto mb-2" data-id="element-1291" />
                <h4 className="font-bold text-slate-800" data-id="element-1292">Direct Manager</h4>
                <p className="text-xs text-slate-500" data-id="element-1293">
                  Validates business need
                </p>
              </div>

              <ArrowRight className="h-6 w-6 text-emerald-400 hidden md:block" data-id="element-1294" />

              <div className="bg-white p-4 rounded-lg border border-emerald-200 shadow-sm w-full md:w-64 text-center relative" data-id="element-1295">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-100 text-emerald-800 text-xs font-bold px-2 py-0.5 rounded-full" data-id="element-1296">
                  Level 2
                </div>
                <Briefcase className="h-8 w-8 text-emerald-500 mx-auto mb-2" data-id="element-1297" />
                <h4 className="font-bold text-slate-800" data-id="element-1298">Dept Head</h4>
                <p className="text-xs text-slate-500" data-id="element-1299">Budget verification</p>
              </div>

              <ArrowRight className="h-6 w-6 text-emerald-400 hidden md:block" data-id="element-1300" />

              <div className="bg-white p-4 rounded-lg border border-emerald-200 shadow-sm w-full md:w-64 text-center relative" data-id="element-1301">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-100 text-emerald-800 text-xs font-bold px-2 py-0.5 rounded-full" data-id="element-1302">
                  Level 3
                </div>
                <CreditCard className="h-8 w-8 text-emerald-500 mx-auto mb-2" data-id="element-1303" />
                <h4 className="font-bold text-slate-800" data-id="element-1304">Finance</h4>
                <p className="text-xs text-slate-500" data-id="element-1305">Final compliance check</p>
              </div>
            </div>
          </div>
        </Section>

        {/* SECTION 4: TRIP MANAGEMENT */}
        <Section number="4" title="Trip Management" color="bg-purple-600" data-id="element-1306">
          <StepCard icon={Plane} title="Confirmed Trips" desc="View itinerary, flight details, and hotel reservations." link="/trips" linkText="View Trips" badge={confirmedCount > 0 ? confirmedCount : null} statusColor="bg-purple-100 text-purple-600" data-id="element-1307" />
          <StepCard icon={FileText} title="Documents" desc="Download e-tickets, booking confirmations, and receipts." link="/trips" linkText="Access Docs" statusColor="bg-purple-100 text-purple-600" data-id="element-1308" />
          <StepCard icon={CreditCard} title="Expense Reporting" desc="Automatically generate expense reports from completed trips." link="/expenses" linkText="View Expenses" statusColor="bg-purple-100 text-purple-600" data-id="element-1309" />
        </Section>

        {/* SECTION 5: MONITORING */}
        <Section number="5" title="Monitoring & Analytics" color="bg-amber-500" data-id="element-1310">
          <StepCard icon={LayoutDashboard} title="Dashboard" desc="Real-time overview of spend, active travelers, and pending actions." link="/" linkText="Go to Dashboard" statusColor="bg-amber-100 text-amber-600" data-id="element-1311" />
          <StepCard icon={Wallet} title="Budget Tracking" desc="Monitor department budgets and forecast future spend." link="/budget" linkText="Check Budget" statusColor="bg-amber-100 text-amber-600" data-id="element-1312" />
          <StepCard icon={Bell} title="Notifications" desc="Stay updated on approvals, flight changes, and policy updates." link="/notifications" linkText="View Alerts" badge={unreadCount > 0 ? unreadCount : null} statusColor="bg-amber-100 text-amber-600" data-id="element-1313" />
        </Section>
      </div>
    </div>;
}